import React from 'react'
import isBefore from 'date-fns/isBefore'
import isEqual from 'date-fns/isEqual'
import isSameMinute from 'date-fns/isSameMinute'
import LocaleUtils from '~/reactapp/shared/DatePicker/localeUtils'

const ONE_MINUTE_MS = 60000

function getFutureDateOrNow(date: Date, now: Date) {
  return isBefore(date, now) ? now : date
}

export default function usePublishDate(initialDate?: Date) {
  const [relativeNow, setRelativeNow] = React.useState(
    LocaleUtils.relativeNow()
  )
  const [date, setDate] = React.useState(
    getFutureDateOrNow(initialDate || relativeNow, relativeNow)
  )

  const proxySetDate = React.useCallback<(date: Date) => void>(
    (date) => {
      if (isBefore(date, relativeNow)) {
        console.warn(
          `usePublishDate warning! You tried to set the date in the past which is considered invalid. It has been set to now (${relativeNow.toString()}) instead.`
        )
      }
      setDate(getFutureDateOrNow(date, relativeNow))
    },
    [relativeNow]
  )

  React.useEffect(() => {
    let timeoutId

    function repeatOnMinuteChange(func) {
      const delayUntilNextMinute = ONE_MINUTE_MS - (Date.now() % ONE_MINUTE_MS)

      timeoutId = setTimeout(() => {
        repeatOnMinuteChange(func)
        func()
      }, delayUntilNextMinute)
    }

    repeatOnMinuteChange(() => {
      const relNow = LocaleUtils.relativeNow()
      const nextDate = getFutureDateOrNow(date, relNow)
      setRelativeNow(relNow)
      if (!isEqual(date, nextDate)) {
        setDate(nextDate)
      }
    })

    return () => {
      clearTimeout(timeoutId)
    }
  }, [date])

  return {
    date,
    setDate: proxySetDate,
    relativeNow,
    isDateNow: isSameMinute(date, relativeNow),
  }
}
